// Note: Rule disabled for easy deletion of the id field on product
// Verified that it works properly despite the linting error
/* eslint-disable no-param-reassign */

import moment from 'moment';
import _ from 'lodash';
//
import { IDeal } from 'store/deals/types';
import { EProductField, IDealProduct, IVolumeOption } from 'store/field-options/types';

const validateProductKeyType = (productKey: string) => {
  return (
    productKey === EProductField.PUSH ||
    productKey === EProductField.PULL ||
    productKey === EProductField.EARLY_TERMINATION ||
    productKey === EProductField.EXTENSION
  );
};

const checkUpdatedDealProductsAndLength = (updatedDeal: IDeal) => {
  return updatedDeal.products && updatedDeal.products.length > 0;
};

const setEmptyStringForUpdatedProduct = (updatedProduct: IVolumeOption) => {
  // Fix empty string values
  if (updatedProduct.basePrice === '') {
    updatedProduct.basePrice = null;
  }
  if (updatedProduct.shellBatna === '') {
    updatedProduct.shellBatna = null;
  }
  if (updatedProduct.custBatna === '') {
    updatedProduct.custBatna = null;
  }
  return updatedProduct;
};

const getProductsUpdated = (updatedDeal: IDeal) => {
  return updatedDeal?.products?.map((product: IDealProduct) => {
    if (product.startDate) {
      product.startDate = moment(product.startDate).isValid()
        ? moment(product.startDate).format('MM/YYYY')
        : product.startDate;
    }
    if (product.endDate) {
      product.endDate = moment(product.endDate).isValid() ? moment(product.endDate).format('MM/YYYY') : product.endDate;
    }
    const productUpdated = { ...product };
    const updatedProductKeys = Object.keys(productUpdated);
    updatedProductKeys.forEach(productKey => {
      const p = productUpdated[productKey];
      if (p && validateProductKeyType(productKey)) {
        // Create new instance
        const updatedProduct = { ...p };

        // Convert day to month format (month = 30 days)
        updatedProduct.notice /= 30;
        productUpdated[productKey] = setEmptyStringForUpdatedProduct(updatedProduct);
      }
    });
    return productUpdated;
  });
};

/**
 * Formats a local deal for the API
 *
 * @param deal - The deal to be formatted
 * @returns Modified deal with dates converted to string types
 */
export function toApi(deal: IDeal): IDeal {
  // Create new reference of deal
  const updatedDeal: IDeal = _.cloneDeep(deal);

  // Remove empty dropdown field
  if (updatedDeal.region === '') {
    delete updatedDeal.region;
  }

  // Remove empty dropdown field
  if (updatedDeal.productLine === '') {
    delete updatedDeal.productLine;
  }

  // Remove unnecessary fields
  delete updatedDeal.modifiedOn;
  delete updatedDeal.modifiedBy;
  delete updatedDeal.createdOn;
  delete updatedDeal.createdBy;

  // Handle empty formula/descriptions
  if (checkUpdatedDealProductsAndLength(updatedDeal)) {
    updatedDeal.products = getProductsUpdated(updatedDeal);
  } else {
    delete updatedDeal.products;
  }
  return updatedDeal;
}

/**
 * Formats a deal from the API for use within the client
 *
 * @param deal The deal object from the API
 * @returns The deal formatted for local manipulation
 */
export function fromApi(deal: IDeal): IDeal {
  // Initialize new deal to return
  const formattedDeal: IDeal = _.cloneDeep(deal);

  const { products } = formattedDeal;
  if (products) {
    products.forEach(product => {
      // Convert string dates (e.g., "01/2020") to JS Date objects
      if (product.startDate) {
        product.startDate = moment(product.startDate, 'MM/YYYY').toDate();
      }
      if (product.endDate) {
        product.endDate = moment(product.endDate, 'MM/YYYY').toDate();
      }

      const productKeys = Object.keys(product);
      productKeys.forEach(productKey => {
        const p = product[productKey];
        if (p && validateProductKeyType(productKey)) {
          const updatedProduct = { ...p };

          // Convert month format to days (month = 30 days)
          updatedProduct.notice *= 30;

          product[productKey] = updatedProduct;
        }
      });
    });
  }
  return formattedDeal;
}
